import { createSelector } from '@reduxjs/toolkit';

import mainConfig from '../../config';
import configLib from '../../configLib';
import {
  PaymentMethodEnum,
  PaymentProcessEnum,
  PaymentStatusEnum,
} from '../enums';
import { Company } from '../models/company';
import {
  AccountTradingPlanStatusEnum,
  Payment,
  TradingAccountStatusesEnum,
  UserTypeRelation,
} from '../models/crm/types';
import {
  ENROLL_REQUEST_STATE,
  IndividualExtendedInfoData,
  IndividualStatusEnum,
} from '../models/enroll';
import { SymbolAndOptionalExchangeCode } from '../models/fundamental/types';
import { Balance } from '../models/trading/types';
import CustomerState from '../store-util/CustomerState';
import { isOneOf } from '../util/DataHelpers';
/**
 * A collection of mostly pseudo selectors / helper functions
 */
import { isEmpty } from '../util/typeGuards';
import { NullableNumber, NullableString } from '../util/types';

import { getCurrentAccount, getCurrentDetailedAccount } from './crm/helpers';
import { UserFavoriteStocks } from './market-data/types';
import { getOldestAdjustmentPayment } from './payment/helpers';
import { RootState } from './index';

const { gatewayEnabled } = configLib;

const individualExtendedInfoSelector = (state: RootState) => state.crm.individualExtendedInfo;
export const isUserVerified = createSelector(
  [ individualExtendedInfoSelector ],
  (info: IndividualExtendedInfoData | null) => isUserVerifiedStatus(info),
);
export function isUserEnrollStepForVerification(info: IndividualExtendedInfoData | null) {
  return !!info && info.individual_status === 103;
}

export function isUserBeforeForVerificationStatus(info: IndividualExtendedInfoData | null) {
  return !!info && info.individual_status < IndividualStatusEnum.ForVerification;
}

export function isUserVerifiedStatus(info: IndividualExtendedInfoData | null) {
  return !!info && info.individual_status >= 200;
}

/**
 * If `state` param is given `favouriteStocks` and `userCorrelationId` are ignored
 * and favourite stocks and user id are taken from `state`.
 */
export const mapUserFavouriteStocksData = (
  favouriteStocks: UserFavoriteStocks | null,
  userCorrelationId: NullableNumber,
  state?: RootState,
): SymbolAndOptionalExchangeCode[] => {
  const theStocks = getFavoriteStocks(state ?? null, favouriteStocks, userCorrelationId);
  return theStocks.map((data: SymbolAndOptionalExchangeCode) => ({
    symbol: data.symbol,
    stockExchangeCode: data.stockExchangeCode,
    countryDesignation: data.countryDesignation,
  }));
};

// Auth
export const isLoggedIn = (state: RootState) => (
  hasValidCookie(state) && state.crm.connected
);

export const isLoginPending = (state: RootState) => (
  state.auth.status === 'connecting'
);

export function hasValidCookie(state: RootState): boolean {
  return state.auth.cookieSessionValid === true;
}

// User events SSE
export function isConnectedUserSSE(state: RootState): boolean {
  return state.ams.isConnectedUserSSE;
}

export function isCookieCheckPending(state: RootState): boolean {
  return state.auth.cookieSessionValid === null;
}

export function isGatewayMDFullyEnabled() {
  const {
    login: gwEnabled,
    subscribe: gwSubscribeEnabled,
  } = gatewayEnabled;

  return (gwEnabled && gwSubscribeEnabled);
}

export function getCompany(symbol: NullableString, state: RootState): Company | undefined {
  if (!symbol || !state?.fundamental?.companies?.find) return undefined;

  return state.fundamental.companies.find(company => company.symbol === symbol) ?? undefined;
}

/**
 * The oldest adjustment payment of the current account - the notifications use it to tell whether
 * the account setup has completed.
 */
export const getOldestAdjustmentOfCurrentAccount = createSelector(
  [ (state: RootState) => state.payment.paymentsAll.data, getCurrentAccount ],
  (transactions, currentAccount) => getOldestAdjustmentPayment(transactions, currentAccount),
);

/**
 * @returns A `Balance` or an Array of [ Balance, index: number ] if `withIndex` is true
 */
export function getCurrentBalance(
  state: RootState,
  withIndex?: boolean,
): Balance | [ Balance, number ] {
  const shouldIncludeIndex = withIndex ?? false;
  const { crm, trading } = state;
  const { balances } = trading;
  const theAccount = getCurrentAccount(state);

  let theIndex = -1;
  let res = balances.find((item, index) => {
    let isAMatch = item.account === theAccount;
    if (isAMatch && theIndex === -1) theIndex = index;
    return isAMatch;
  });

  if (!res) {
    res = {} as any; // force empty object - in order to be able to show availBP, etc. props as invalid values (undefined)

    // This is an exception for test environment (and mocked - for testing and mocked mode)
    const isTestOrMockedEnv = isOneOf(mainConfig.currentEnv, [ 'mocked', 'test' ]);
    const hasOneBalance = balances.length === 1;
    const hasOneAccount = crm.tradingAccounts.length === 1;
    if (isTestOrMockedEnv && hasOneBalance && hasOneAccount) {
      res = balances[0];
      return shouldIncludeIndex ? [ res, 0 ] : res;
    }

    console.log(`[selectors] getCurrentBalance - could not find balance data for account '${theAccount}'`);
  }

  return shouldIncludeIndex ? [ res!, theIndex ] : res!;
}

/**
 * Whether the account has passed its current trading plan phase.
 */
export function getIsTradingPlanCompleted(state: RootState): boolean {
  return state.closeout.accountStatusData.isTradingPlanCompleted
    || getCurrentDetailedAccount(state)?.account_trading_plan_status === AccountTradingPlanStatusEnum.Successful;
}

export function isCurrentAccountActive(state: RootState): boolean {
  return getCurrentDetailedAccount(state)?.account_status === TradingAccountStatusesEnum.Active;
}

export function getCurrentBuyingPower(state: RootState): number {
  const balance = getCurrentBalance(state) as Balance;
  const { sumOfPendingOrders } = state.trading;
  const pendingForAccount = sumOfPendingOrders[balance.account];

  return pendingForAccount ? balance.availBp - pendingForAccount : (balance.availBp ?? 0);
}

export function isAdjustment(payment: Payment) {
  return (payment && payment.payment_process === PaymentProcessEnum.Adjustment);
}

export function isCardDeposit(payment: Payment) {
  return (
    payment
    && payment.payment_process === PaymentProcessEnum.Deposit
    && payment.payment_method === PaymentMethodEnum.Card
  );
}

export function isPendingCardTransfer(payment: Payment) {
  return (
    payment
    && payment.payment_status === PaymentStatusEnum.Pending
    && payment.payment_method === PaymentMethodEnum.Card
  );
}

export function isWithdrawal(payment: Payment) {
  return (payment && payment.payment_process === PaymentProcessEnum.Withdrawal);
}

export function isPendingTransaction(payment: Payment) {
  return (payment && payment.payment_status <= PaymentStatusEnum.Pending);
}

/**
 * If `state` param is given `favouriteStocks` and `userCorrelationId` are ignored
 * and favourite stocks and user id are taken from `state`.
 */
export function getFavoriteStocks(
  state: RootState | null,
  favouriteStocks?: UserFavoriteStocks | null,
  userCorrelationId?: NullableNumber,
) {
  const allFavorites: UserFavoriteStocks | undefined | null = state?.marketData.favouriteStocks ?? favouriteStocks;
  const id = state?.crm?.individualExtendedInfo?.user_correlation_id ?? userCorrelationId;
  if (!allFavorites || !id) {
    return [];
  }

  const theStocks = allFavorites[id];
  if (isEmpty(theStocks)) {
    return [];
  }

  return allFavorites[id];
}

export function getRecentlyViewedStocks(state: RootState) {
  const id = state?.crm?.individualExtendedInfo?.user_correlation_id;

  if (!state.marketData.recentlyViewedStocks || !id) {
    return [];
  }

  const theStocks = state.marketData.recentlyViewedStocks[id];
  if (isEmpty(theStocks)) {
    return [];
  }

  return theStocks;
}

export const isSymbolETF = (symbol: NullableString, state: RootState): boolean => {
  if (!symbol) return false;
  const issueType = getCompany(symbol, state)?.issueType;

  return issueType === 'et';
};

export const getEarningsNotificationsPortfolioSymbols = createSelector(
  [
    (state: RootState) => state.reporting.openPositions,
    getCurrentAccount,
  ],
  (openPositions, accountReferenceId): string[] => {
    const symbols = openPositions
      .filter(position => !accountReferenceId || position.accountReferenceId === accountReferenceId)
      .map(position => position.symbol);

    return [...new Set(symbols)];
  },
);
